import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import useSEO from '@/hooks/useSEO';
import { motion, useInView } from 'framer-motion';
import { 
  ArrowRight, 
  TrendingUp, 
  Clock, 
  Shield, 
  BarChart3,
  Users,
  Home,
  Star,
  Phone,
  ChevronRight
} from 'lucide-react';
import AuditForm from '@/components/AuditForm';

// Team photos
const TEAM_PHOTOS = {
  equipe1: "https://customer-assets.emergentagent.com/job_690b4278-c95d-4a2f-aec5-d7977a254dc4/artifacts/xaq1ywtw_This_person_is_202601212104.jpeg",
  equipe2: "https://customer-assets.emergentagent.com/job_690b4278-c95d-4a2f-aec5-d7977a254dc4/artifacts/tyyupd9e_This_person_is_202601212105.jpeg",
  equipe3: "https://customer-assets.emergentagent.com/job_690b4278-c95d-4a2f-aec5-d7977a254dc4/artifacts/xk07b54i_The_three_people_202601221014.jpeg"
};

// Abstract texture
const TEXTURE_URL = "https://images.unsplash.com/photo-1761437855740-c894da924d79?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxnb2xkJTIwYWJzdHJhY3QlMjBsaW5lcyUyMGRhcmslMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc2OTM0NDI2N3ww&ixlib=rb-4.1.0&q=85";

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const staggerChildren = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Section component with scroll animation
const Section = ({ children, className = '', id }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.7 }}
      className={className}
      id={id}
    >
      {children}
    </motion.section>
  );
};

const HomePage = () => {
  useSEO({
    title: 'Confort Hôte | Conciergerie Airbnb Ille-et-Vilaine',
    description: 'Confort Hôte - Conciergerie Airbnb en Ille-et-Vilaine. Pilotage de locations courte durée à Fougères, Rennes, Vitré. Commission 18% HT. Audit gratuit.',
    keywords: 'conciergerie Airbnb Fougères, conciergerie Rennes, conciergerie Vitré, conciergerie Dol-de-Bretagne, conciergerie Pleine-Fougères, Confort Hôte, Ille-et-Vilaine',
  });

  const cities = [
    { slug: 'fougeres', name: 'Fougères' },
    { slug: 'rennes', name: 'Rennes' },
    { slug: 'vitre', name: 'Vitré' },
    { slug: 'dol-de-bretagne', name: 'Dol-de-Bretagne' },
    { slug: 'pleine-fougeres', name: 'Pleine-Fougères' },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        {/* Background with overlay */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-20"
            style={{ backgroundImage: `url(${TEXTURE_URL})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-transparent to-[#050505]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32 md:py-40">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            {/* Left Content */}
            <div className="lg:col-span-7">
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-6"
              >
                Conciergerie Airbnb · Ille-et-Vilaine
              </motion.p>
              
              <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-playfair font-medium tracking-tight leading-[1.1] text-[#F5F5F5] mb-8"
              >
                Votre bien mérite<br/>
                <span className="text-gold-gradient">un pilotage</span><br/>
                d'excellence
              </motion.h1>
              
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-lg md:text-xl text-[#A3A3A3] font-light leading-relaxed max-w-xl mb-10"
              >
                Confort Hôte transforme votre location courte durée en source de revenus optimisée. 
                Sans stress, sans contrainte, avec un interlocuteur local dédié.
              </motion.p>

              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="flex flex-col sm:flex-row gap-4"
              >
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 bg-[#C5A059] text-black px-8 py-4 text-sm uppercase tracking-[0.15em] font-semibold hover:bg-[#D4AF37] transition-colors duration-300"
                  data-testid="hero-cta-audit"
                >
                  <span>Demander un audit gratuit</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href="tel:0689224402"
                  className="inline-flex items-center justify-center gap-2 border border-[#C5A059]/30 text-[#C5A059] px-8 py-4 text-sm uppercase tracking-[0.15em] hover:border-[#C5A059] hover:bg-[#C5A059]/5 transition-colors duration-300"
                  data-testid="hero-cta-phone"
                >
                  <Phone className="w-4 h-4" />
                  <span>06 89 22 44 02</span>
                </a>
              </motion.div>
            </div>

            {/* Right Stats */}
            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 }}
              className="lg:col-span-5"
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="glass-card p-6 md:p-8">
                  <p className="text-3xl md:text-4xl font-playfair text-[#C5A059] mb-2">18%</p>
                  <p className="text-xs uppercase tracking-wider text-[#A3A3A3]">Commission HT</p>
                </div>
                <div className="glass-card p-6 md:p-8">
                  <p className="text-3xl md:text-4xl font-playfair text-[#C5A059] mb-2">+33%</p>
                  <p className="text-xs uppercase tracking-wider text-[#A3A3A3]">Revenus moyens</p>
                </div>
                <div className="glass-card p-6 md:p-8">
                  <p className="text-3xl md:text-4xl font-playfair text-[#C5A059] mb-2">20+</p>
                  <p className="text-xs uppercase tracking-wider text-[#A3A3A3]">Plateformes</p>
                </div>
                <div className="glass-card p-6 md:p-8">
                  <p className="text-3xl md:text-4xl font-playfair text-[#C5A059] mb-2">7/7</p>
                  <p className="text-xs uppercase tracking-wider text-[#A3A3A3]">Assistance</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="w-[1px] h-16 bg-gradient-to-b from-[#C5A059] to-transparent" />
        </motion.div>
      </section>

      {/* Problem Section */}
      <Section className="py-24 md:py-32 bg-[#0A0A0A]" id="probleme">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-6">
                Le constat
              </p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-playfair font-normal tracking-tight leading-tight text-[#F5F5F5] mb-8">
                Gérer seul, c'est<br/>
                <span className="italic">chronophage</span>
              </h2>
              <div className="space-y-6">
                {[
                  "Messages à toute heure, check-in tardifs",
                  "Ménage, linge, petites réparations",
                  "Tarification à l'aveugle, revenus sous-optimisés",
                  "Réglementation complexe et changeante"
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-8 h-[1px] bg-[#C5A059] mt-3" />
                    <p className="text-lg text-[#A3A3A3] font-light">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src={TEAM_PHOTOS.equipe3}
                  alt="L'équipe Confort Hôte en action"
                  className="w-full h-full object-cover img-desaturate"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-[#050505] p-6 border border-[#262626]">
                <p className="text-2xl font-playfair text-[#C5A059] mb-1">Gérard Hany</p>
                <p className="text-sm text-[#A3A3A3]">Fondateur, Confort Hôte</p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Solution Section */}
      <Section className="py-24 md:py-32" id="solution">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-6">
              Notre réponse
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-playfair font-normal tracking-tight leading-tight text-[#F5F5F5] mb-6">
              Un pilotage structuré,<br/>local et humain
            </h2>
            <p className="text-lg text-[#A3A3A3] font-light max-w-2xl mx-auto">
              Confort Hôte prend en charge l'intégralité de votre location. 
              Vous gardez la main, nous faisons le travail.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: TrendingUp,
                title: "Revenue Management",
                description: "Tarification dynamique ajustée quotidiennement selon le marché local et les événements."
              },
              {
                icon: Home,
                title: "Pilotage Opérationnel",
                description: "Ménage professionnel, blanchisserie, maintenance préventive et curative."
              },
              {
                icon: Users,
                title: "Assistance Voyageur",
                description: "Réponse rapide 7j/7, check-in flexible, résolution des incidents."
              },
              {
                icon: BarChart3,
                title: "Diffusion Multicanal",
                description: "Visibilité optimale sur plus de 20 plateformes (Airbnb, Booking, etc.)."
              },
              {
                icon: Shield,
                title: "Conformité",
                description: "Déclarations, taxes de séjour, respect de la réglementation locale."
              },
              {
                icon: Clock,
                title: "Reporting Transparent",
                description: "Tableau de bord mensuel, suivi des revenus et indicateurs clés."
              }
            ].map((feature, index) => (
              <div 
                key={index}
                className="glass-card p-8 group hover:border-[#C5A059]/30 transition-colors duration-500"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-[#C5A059]/10 text-[#C5A059] mb-6 group-hover:bg-[#C5A059]/20 transition-colors duration-300">
                  <feature.icon className="w-6 h-6" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-playfair text-[#F5F5F5] mb-3">{feature.title}</h3>
                <p className="text-[#A3A3A3] font-light leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/conciergerie"
              className="inline-flex items-center gap-2 text-[#C5A059] hover:text-[#D4AF37] transition-colors duration-300"
              data-testid="link-discover-offer"
            >
              <span className="text-sm uppercase tracking-wider">Découvrir l'offre complète</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </Section>

      {/* Case Study Section */}
      <Section className="py-24 md:py-32 bg-[#0A0A0A]" id="etude-de-cas">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-6">
              Étude de cas
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-playfair font-normal tracking-tight leading-tight text-[#F5F5F5]">
              Longère T5 · Région de Fougères
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Before */}
            <div className="bg-[#121212] border-l-4 border-[#A3A3A3] p-8 md:p-12">
              <p className="text-xs uppercase tracking-[0.3em] text-[#A3A3A3] mb-8">Avant Confort Hôte</p>
              <div className="space-y-6">
                <div>
                  <p className="text-4xl md:text-5xl font-playfair text-[#A3A3A3] mb-2">54 000 €</p>
                  <p className="text-sm text-[#A3A3A3]/70">Revenus annuels</p>
                </div>
                <div className="h-[1px] bg-[#262626]" />
                <ul className="space-y-3 text-[#A3A3A3] font-light">
                  <li className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 bg-[#A3A3A3] rounded-full" />
                    Diffusion Airbnb uniquement
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 bg-[#A3A3A3] rounded-full" />
                    Tarification fixe toute l'année
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 bg-[#A3A3A3] rounded-full" />
                    Gestion chronophage au quotidien
                  </li>
                </ul>
              </div>
            </div>

            {/* After */}
            <div className="bg-[#121212] border-l-4 border-[#C5A059] p-8 md:p-12">
              <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-8">Avec Confort Hôte</p>
              <div className="space-y-6">
                <div>
                  <p className="text-4xl md:text-5xl font-playfair text-[#C5A059] mb-2">72 000 €</p>
                  <p className="text-sm text-[#A3A3A3]">Revenus annuels · <span className="text-[#C5A059]">+33%</span></p>
                </div>
                <div className="h-[1px] bg-[#262626]" />
                <ul className="space-y-3 text-[#F5F5F5] font-light">
                  <li className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 bg-[#C5A059] rounded-full" />
                    Diffusion multicanal (20+ plateformes)
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 bg-[#C5A059] rounded-full" />
                    Revenue management dynamique
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 bg-[#C5A059] rounded-full" />
                    Sérénité totale pour le propriétaire
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Cities Section */}
      <Section className="py-24 md:py-32" id="zones">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-6">
                Zones d'intervention
              </p>
              <h2 className="text-3xl sm:text-4xl font-playfair font-normal tracking-tight text-[#F5F5F5]">
                Un ancrage local fort
              </h2>
            </div>
            <p className="text-[#A3A3A3] font-light mt-4 md:mt-0 max-w-md">
              Notre connaissance du terrain en Ille-et-Vilaine fait la différence.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {cities.map((city) => (
              <Link
                key={city.slug}
                to={`/conciergerie/${city.slug}`}
                className="group relative bg-[#0A0A0A] border border-[#262626] p-8 hover:border-[#C5A059]/30 transition-colors duration-500"
                data-testid={`city-link-${city.slug}`}
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-playfair text-[#F5F5F5] group-hover:text-[#C5A059] transition-colors duration-300">
                    {city.name}
                  </h3>
                  <ChevronRight className="w-5 h-5 text-[#A3A3A3] group-hover:text-[#C5A059] group-hover:translate-x-1 transition-transform duration-300" />
                </div>
                <p className="text-sm text-[#A3A3A3] mt-2">
                  Conciergerie Airbnb à {city.name}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </Section>

      {/* Team Section */}
      <Section className="py-24 md:py-32 bg-[#0A0A0A]" id="equipe">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-6">
                L'équipe
              </p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-playfair font-normal tracking-tight leading-tight text-[#F5F5F5] mb-8">
                Des professionnels<br/>du terrain
              </h2>
              <p className="text-lg text-[#A3A3A3] font-light leading-relaxed mb-8">
                Confort Hôte, c'est avant tout une équipe passionnée par l'accueil et 
                l'excellence opérationnelle. Nous connaissons chaque rue, chaque particularité 
                de notre territoire.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <Star className="w-5 h-5 text-[#C5A059] mt-1" />
                  <p className="text-[#F5F5F5]">Gérard Hany, fondateur et ancien hôtelier</p>
                </div>
                <div className="flex items-start gap-4">
                  <Star className="w-5 h-5 text-[#C5A059] mt-1" />
                  <p className="text-[#F5F5F5]">Une équipe locale et réactive</p>
                </div>
                <div className="flex items-start gap-4">
                  <Star className="w-5 h-5 text-[#C5A059] mt-1" />
                  <p className="text-[#F5F5F5]">Interlocuteur unique et dédié</p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="aspect-[3/4] overflow-hidden">
                  <img 
                    src={TEAM_PHOTOS.equipe1}
                    alt="Membre de l'équipe"
                    className="w-full h-full object-cover img-grayscale"
                  />
                </div>
              </div>
              <div className="space-y-4 mt-8">
                <div className="aspect-[3/4] overflow-hidden">
                  <img 
                    src={TEAM_PHOTOS.equipe2}
                    alt="Membre de l'équipe"
                    className="w-full h-full object-cover img-grayscale"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* CTA / Audit Form Section */}
      <Section className="py-24 md:py-32" id="audit">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-6">
              Passez à l'action
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-playfair font-normal tracking-tight leading-tight text-[#F5F5F5] mb-6">
              Demandez votre audit gratuit
            </h2>
            <p className="text-lg text-[#A3A3A3] font-light max-w-2xl mx-auto">
              Obtenez une estimation personnalisée du potentiel locatif de votre bien. 
              Sans engagement.
            </p>
          </div>

          <div className="max-w-xl mx-auto">
            <div className="glass-card p-8 md:p-12">
              <AuditForm />
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default HomePage;
